"use client";

// App shell. One nav column — icon AND label on the same row — floating on the
// canvas as a card, with the content beside it as a second card.
//
// This replaces a two-column rail+panel arrangement that listed the same three
// destinations twice: a 44px icon-only rail (Dashboard/Jobs/Companies as marks)
// beside a 180px labelled panel repeating them in words. Two columns for three
// links, and the icon column carried no information the labelled one didn't.
// The rule now: if a destination needs a label to be understood, the label
// belongs on the same row as its icon.
//
// It is built from the Landing page's vocabulary rather than a parallel one —
// the same --shell-canvas / --shell-panel / --card surface ladder, the same
// hairline --border as the only separation device (the Landing's deck comment
// states the rule: "Separation is the border and the surface step — never a
// shadow"), the same --primary accent, and the same expo-out settle curve the
// rest of the app animates on. NO dark sidebar, and no backdrop blur: the
// Landing has neither. Its surfaces are the light warm greys above, and
// inventing a dark chrome panel for the app would have made the two halves of
// the product look like two products — the exact failure this pass exists to
// fix. (The brief assumed the Landing had a dark background treatment with a
// glow. It does not: grep for `shadow`/`blur` in landing.tsx returns only the
// comment forbidding shadows. The tokens below are the real ones.)
//
// Radii are deliberately NOT uniform — a single radius everywhere is the
// flattest tell in this kind of redesign:
//
//   rounded-lg   (8px)   the shell plane itself — the outer container
//   rounded-card (12px)  content and job cards — the things you read
//   rounded-md   (6px)   nav rows, control-sized — the things you press
//   rounded-pill         badges and chips
//
// The one piece of boldness here is the ACTIVE INDICATOR: a single element
// shared by whichever row is current (framer-motion `layoutId`), so changing
// route slides the highlight from the old row to the new one instead of
// blinking out in one place and on in another. It is the same gesture the Jobs
// page uses to move its filter tint between chips and the Landing uses between
// its previews — one travelling object, three surfaces. Everything else in the
// shell is quiet: a background fade on hover, a small icon response, nothing
// that moves on its own.

import Image from "next/image";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useState, type ComponentType } from "react";
import {
  BriefcaseIcon,
  BuildingIcon,
  CalendarIcon,
  DocumentIcon,
  HomeIcon,
  MoonIcon,
  SparklesIcon,
  SunIcon,
} from "@/app/components/icons";
import { useAuth } from "@/app/lib/auth-context";
import { usePrefersReducedMotion } from "@/app/lib/use-reduced-motion";
import { useTheme } from "@/app/components/theme-provider";
import { NotificationBell } from "@/app/components/notification-bell";
import { DUR_BASE, EASE_SETTLE } from "@/app/components/motion";
import { cn } from "@/app/lib/cn";

interface NavItem {
  href: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
}

// The single source of truth for navigation. A future section is one entry
// here; there is no second column to keep in sync. Settings carries the
// calendar connections (Google OAuth + the Apple/Google Calendar ICS feed).
// Documents joins with a static-mock page until the file-storage backend
// lands — the route exists, the shell doesn't care what backs it.
const NAV_ITEMS: NavItem[] = [
  { href: "/", label: "Dashboard", icon: HomeIcon },
  { href: "/jobs", label: "Jobs", icon: BriefcaseIcon },
  { href: "/companies", label: "Companies", icon: BuildingIcon },
  { href: "/documents", label: "Documents", icon: DocumentIcon },
  { href: "/interview-practice", label: "Interview Prep", icon: SparklesIcon },
  { href: "/settings", label: "Settings", icon: CalendarIcon },
];

// /auth/callback is chrome-less too: it is the Google OAuth round-trip's
// landing spot, and the finish-sign-in card reads better with the same slim
// brand bar the other auth routes use.
const CHROMELESS_ROUTES = new Set(["/login", "/register", "/auth/callback"]);

// A nav row's box: 44px tall so the target is thumb-sized even on a pointer.
// The label sits at 13px/500 and takes --foreground/70 when idle — NOT
// --muted-foreground, which measures 4.43:1 on --shell-panel and misses AA for
// text by a hair (the same miss the Badge primitive documents). An alpha of
// --foreground composites over whatever it lands on and clears it with room.
// font-weight is NOT in this base string: the active row is semibold and the
// idle row medium, and two weights in one class list is a coin flip decided by
// Tailwind's generated order rather than by this file. Each branch states its
// own.
const NAV_ROW =
  "group/nav relative flex min-h-11 items-center gap-3 rounded-md px-3 text-[13px] transition-colors duration-150";

// The travelling highlight — a SOLID filled accent plane behind the whole row,
// not a thin bar beside it.
//
// The earlier version tinted the row (--primary at 10%) and hung a 3px bar on
// its leading edge. That is a "selection accent", and at a glance it reads as
// decoration: the row you are on and the rows you are not are the same value,
// one with a marker. The owner asked for the fill itself to move, so the
// indicator is now a full-bleed opaque --primary plate with the label and icon
// knocked out in --primary-foreground — the palette's own "text that sits on
// --primary" token, which is white on the light accent (#2563eb) and near-black
// on the dark one (#60a5fa). The contrast is carried by the token pair rather
// than computed here.
//
// It still travels. `layoutId` makes the plate a single shared element, so
// navigating slides it from the old row to the new one instead of blinking out
// in one place and on in another — at DUR_BASE, the same settle curve every
// other state change in the app uses. The `layoutId` is a parameter because
// the drawer and the desktop column are mounted at the same time (the desktop
// aside is `hidden lg:flex`, which is display:none, not unmounted); two
// elements sharing one layoutId would fight over the same animation.
function ActiveIndicator({ layoutId, reduced }: { layoutId: string; reduced: boolean }) {
  return (
    <motion.span
      layoutId={layoutId}
      aria-hidden="true"
      className="absolute inset-0 rounded-md bg-primary"
      transition={reduced ? { duration: 0.01 } : { duration: DUR_BASE, ease: EASE_SETTLE }}
    />
  );
}

function isActive(pathname: string, href: string) {
  // "/" is the dashboard; every other route is an exact-prefix match so a
  // future nested route (/jobs/settings) keeps its section highlighted.
  return href === "/" ? pathname === "/" : pathname === href || pathname.startsWith(`${href}/`);
}

function wordmark(href: string, className?: string) {
  return (
    <Link href={href} aria-label="JobYab — home" className={cn("flex flex-none items-center", className)}>
      <Image src="/logo.png" alt="JobYab" width={108} height={36} priority className="h-7 w-auto" />
    </Link>
  );
}

function ThemeToggle({ className }: { className?: string }) {
  const { dark, toggle } = useTheme();

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={dark ? "Switch to light mode" : "Switch to dark mode"}
      aria-pressed={dark}
      title={dark ? "Light mode" : "Dark mode"}
      className={cn(
        "flex h-11 w-11 flex-none cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors duration-150 hover:bg-muted hover:text-foreground lg:h-8 lg:w-8",
        className
      )}
    >
      {dark ? <SunIcon className="h-4 w-4" /> : <MoonIcon className="h-4 w-4" />}
    </button>
  );
}

function Avatar({ name, large }: { name: string; large?: boolean }) {
  const initials =
    name
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((word) => word.charAt(0).toUpperCase())
      .join("") || "?";

  return (
    <span
      aria-hidden="true"
      className={cn(
        "flex flex-none items-center justify-center rounded-pill bg-cta font-bold text-cta-foreground",
        large ? "h-12 w-12 text-base" : "h-8 w-8 text-xs"
      )}
    >
      {initials}
    </span>
  );
}

/* --------------------------------------------------------------- desktop */

export function NavRow({
  item,
  active,
  reduced,
  layoutId = "shell-nav-active",
  onNavigate,
}: {
  item: NavItem;
  active: boolean;
  reduced: boolean;
  layoutId?: string;
  /** Fired on click — the mobile drawer uses it to dismiss itself, which keeps
   * the close out of a pathname effect (a synchronous setState in an effect
   * body, which this repo's lint rule rejects). */
  onNavigate?: () => void;
}) {
  const Icon = item.icon;

  return (
    <Link
      href={item.href}
      onClick={onNavigate}
      aria-current={active ? "page" : undefined}
      className={cn(
        NAV_ROW,
        active
          ? "font-semibold text-primary-foreground"
          : "font-medium text-foreground/70 hover:bg-muted hover:text-foreground"
      )}
    >
      {active && <ActiveIndicator layoutId={layoutId} reduced={reduced} />}
      <Icon
        className={cn(
          "relative h-[15px] w-[15px] flex-none transition-transform duration-150 group-hover/nav:scale-110",
          active ? "text-primary-foreground" : "text-muted-foreground group-hover/nav:text-foreground"
        )}
      />
      <span className="relative">{item.label}</span>
    </Link>
  );
}

// The account block is a raised chip rather than a section under a divider
// line: an inset --card plane on the --shell-panel sidebar. The brief asks for
// "no hard divider lines", and a surface step is how the Landing separates
// things anyway.
function AccountBlock() {
  const { user, loading, logout } = useAuth();
  const router = useRouter();

  function handleLogout() {
    logout();
    router.push("/");
  }

  if (loading) return null;

  if (!user) {
    return (
      <Link
        href="/login"
        className="block w-full cursor-pointer rounded-button bg-cta px-3.5 py-2.5 text-center text-[13px] font-medium text-cta-foreground transition-colors duration-150 hover:bg-cta-hover"
      >
        Login
      </Link>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2.5 px-0.5">
        <Avatar name={user.full_name || user.email} />
        <div className="min-w-0">
          <p className="truncate text-[13px] font-semibold text-foreground">
            {user.full_name || user.email}
          </p>
          <p className="truncate text-[11px] text-muted-foreground">{user.email}</p>
        </div>
      </div>
      <button
        type="button"
        onClick={handleLogout}
        className="w-full cursor-pointer rounded-button border border-border bg-card px-2 py-2 text-xs font-medium text-foreground/70 transition-colors duration-150 hover:border-muted-foreground/40 hover:bg-muted hover:text-foreground"
      >
        Log out
      </button>
    </div>
  );
}

function SidebarPanel() {
  const pathname = usePathname();
  const reduced = usePrefersReducedMotion();

  return (
    <div className="flex h-full w-full flex-col rounded-lg border border-border bg-shell-panel p-2">
      {/* Brand, then the two app-level controls. The theme toggle used to live
          at the bottom of the old icon-only rail, which this pass deleted — so
          it moves up here rather than disappearing with it. Desktop and mobile
          each carry exactly one instance; they are never both on screen. */}
      <div className="mb-3 flex h-9 flex-none items-center justify-between px-1.5">
        {wordmark("/")}
        <div className="flex items-center gap-0.5">
          <NotificationBell />
          <ThemeToggle />
        </div>
      </div>

      <nav aria-label="Primary" className="flex flex-1 flex-col gap-0.5 overflow-y-auto">
        {NAV_ITEMS.map((item) => (
          <NavRow
            key={item.href}
            item={item}
            active={isActive(pathname, item.href)}
            reduced={reduced}
          />
        ))}
      </nav>

      {/* The account chip is pinned to the bottom, on its own --card plane.
          It carries a hairline as well as the surface step, because the step
          alone does not survive the dark palette: there --card and
          --shell-panel are both #131313, so the chip would dissolve into the
          panel and leave the account area floating with no boundary. At /60 the
          hairline is a whisper on the light panel and a definition on the dark
          one — one mechanism, both themes. */}
      <div className="mt-3 flex-none rounded-md border border-border/60 bg-card p-2">
        <AccountBlock />
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------- mobile */

// The collapse mechanism is the one this codebase already had — a floating top
// bar plus a fixed bottom nav below `lg` — KEPT as the primary mobile
// navigation, because that is what the live site actually shows and a redesign
// that replaces a working thumb-reachable bar with a hidden drawer is a
// regression dressed as a feature. The bottom bar is what gets the restyle.
//
// The drawer is ADDED beside it, for the things a three-tab bar cannot hold:
// the labelled destinations, the theme toggle, and the account block. It is
// opened and closed by the hamburger in the top bar — which outranks the sheet
// and so morphs in place, on screen, for the whole gesture — and also closed by
// the backdrop, Escape, or choosing a destination.
function HamburgerButton({
  open,
  onClick,
  reduced,
}: {
  open: boolean;
  onClick: () => void;
  reduced: boolean;
}) {
  const t = reduced ? { duration: 0.01 } : { duration: DUR_BASE, ease: EASE_SETTLE };

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={open ? "Close menu" : "Open menu"}
      aria-expanded={open}
      className="flex h-11 w-11 flex-none cursor-pointer items-center justify-center rounded-md text-foreground transition-colors duration-150 hover:bg-muted"
    >
      {/* Three 2px rules in an 18×14 box. Open, the outer two rotate to ±45°
          about that box's centre — the top rule travels down 6px, the bottom
          one travels up 6px, and both meet the middle rule's line, which fades
          to nothing. The result is a symmetric X, and because every value is
          symmetric about the middle the reverse plays exactly backwards. */}
      <span className="relative block h-[14px] w-[18px]" aria-hidden="true">
        <motion.span
          className="absolute left-0 top-0 block h-[2px] w-full rounded-pill bg-current"
          animate={open ? { y: 6, rotate: 45 } : { y: 0, rotate: 0 }}
          transition={t}
        />
        <motion.span
          className="absolute left-0 top-[6px] block h-[2px] w-full rounded-pill bg-current"
          animate={{ opacity: open ? 0 : 1 }}
          transition={t}
        />
        <motion.span
          className="absolute bottom-0 left-0 block h-[2px] w-full rounded-pill bg-current"
          animate={open ? { y: -6, rotate: -45 } : { y: 0, rotate: 0 }}
          transition={t}
        />
      </span>
    </button>
  );
}

// The top bar carries identity and the two controls; the account ACTIONS
// (logout) moved into the drawer with the rest of the labelled navigation, so
// there is no longer a Logout button competing with the hamburger for the same
// 44px row.
//
// It is also the ONLY thing that outranks the drawer (z-50 against the drawer's
// z-40). The panel is w-72 anchored right, so it covers the bar's right end —
// including the hamburger — and an overlay that hides its own trigger makes the
// rotate-to-X morph unwatchable: the button silently finished animating behind
// the sheet and the only X on screen belonged to the drawer. Lifting the whole
// bar above the panel and its backdrop keeps the trigger on screen and live for
// the entire gesture, open and close. The bar paints bg-shell-canvas (the page
// colour) rather than being transparent, because the panel and the 55% backdrop
// sit directly underneath it: without an opaque fill the sheet's top-right
// corner and whatever it renders up there would ghost through the 8px gutters
// either side of the header card.
//
// The bar's height and the sheet's top clearance, kept as a pair. The sheet is
// full-height and slides UNDER the bar, so whatever it puts in the first 74px is
// both hidden and unclickable — deriving its padding from the same number is what
// keeps the two from drifting apart. A bare pt-[86px] would rot the next time a
// control is added to the bar.
const MOBILE_BAR_H = 74; // pt-2 (8) + card py-2.5 (20) + the 44px control row + 2px border
const SHEET_TOP_PAD = MOBILE_BAR_H + 12; // 12px of air below the bar

function MobileTopBar({
  onToggleMenu,
  menuOpen,
}: {
  onToggleMenu: () => void;
  menuOpen: boolean;
}) {
  const { user, loading } = useAuth();
  const reduced = usePrefersReducedMotion();

  return (
    <div className="relative z-50 bg-shell-canvas px-2 pt-2 lg:hidden">
      <div className="flex items-center justify-between gap-2 rounded-card border border-border bg-card px-3 py-2.5">
        {wordmark("/")}
        <div className="flex items-center gap-1">
          <NotificationBell />
          {loading ? null : user ? (
            <Avatar name={user.full_name || user.email} />
          ) : (
            <Link
              href="/login"
              className="flex min-h-11 cursor-pointer items-center rounded-button bg-cta px-3 text-xs font-medium text-cta-foreground transition-colors duration-150 hover:bg-cta-hover"
            >
              Login
            </Link>
          )}
          <ThemeToggle />
          <HamburgerButton open={menuOpen} onClick={onToggleMenu} reduced={reduced} />
        </div>
      </div>
    </div>
  );
}

function MobileDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const pathname = usePathname();
  const reduced = usePrefersReducedMotion();

  useEffect(() => {
    if (!open) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  const panelTransition = reduced
    ? { duration: 0.01 }
    : // base + 40ms: the panel is the slowest thing in this gesture, so the
      // backdrop has visibly arrived before the sheet finishes settling.
      { duration: DUR_BASE + 0.04, ease: EASE_SETTLE };

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <motion.button
            type="button"
            aria-label="Close menu"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={reduced ? { duration: 0.01 } : { duration: DUR_BASE, ease: EASE_SETTLE }}
            className="absolute inset-0 cursor-pointer bg-black/55"
          />
          <motion.aside
            role="dialog"
            aria-modal="true"
            aria-label="Menu"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={panelTransition}
            // The sheet runs the full height and passes UNDER the bar, so
            // anything it put in that first band would be hidden behind the bar
            // and — because the bar outranks it — unclickable beneath it. That is
            // why the wordmark and the sheet's own X are gone from up here: a
            // duplicate logo nobody sees, and a close button Playwright could not
            // click from underneath the bar ("subtree intercepts pointer events"),
            // i.e. a focusable ghost. The close control is the header's
            // hamburger, which sits above the sheet and morphs in place; the
            // backdrop and Escape are the other two ways out.
            className="absolute inset-y-0 right-0 flex w-72 max-w-[84vw] flex-col border-l border-border bg-shell-panel px-3 pb-3"
            style={{ paddingTop: SHEET_TOP_PAD }}
          >
            <nav aria-label="Menu" className="flex flex-col gap-0.5">
              {NAV_ITEMS.map((item) => (
                <NavRow
                  key={item.href}
                  item={item}
                  active={isActive(pathname, item.href)}
                  reduced={reduced}
                  layoutId="shell-drawer-active"
                  onNavigate={onClose}
                />
              ))}
            </nav>

            <div className="mt-auto flex flex-col gap-3 rounded-card border border-border/60 bg-card p-3">
              <AccountBlock />
            </div>
          </motion.aside>
        </div>
      )}
    </AnimatePresence>
  );
}

function MobileBottomNav() {
  const pathname = usePathname();

  return (
    <nav
      aria-label="Main"
      className="fixed inset-x-2 bottom-2 z-20 rounded-card border border-border bg-card p-1 lg:hidden"
    >
      <div
        className="grid pb-[max(env(safe-area-inset-bottom),0px)]"
        style={{ gridTemplateColumns: `repeat(${NAV_ITEMS.length}, minmax(0, 1fr))` }}
      >
        {NAV_ITEMS.map((item) => {
          const active = isActive(pathname, item.href);
          const Icon = item.icon;
          return (
            <Link
              key={item.href}
              href={item.href}
              aria-current={active ? "page" : undefined}
              // The active tab takes the accent on the icon AND the label —
              // one hue on both marks, crossfading at `fast`. No background
              // chip: below `lg` the bar is three items wide and a filled plate
              // under one of them would outweigh the content it navigates. The
              // hue is the state, which is the same rule the desktop sidebar
              // follows in the opposite direction (there the fill IS the hue).
              className={cn(
                "flex min-h-11 flex-col items-center justify-center gap-1 rounded-md py-1.5 text-[11px] transition-colors duration-150",
                active
                  ? "font-semibold text-primary"
                  : "font-medium text-foreground/70 hover:text-foreground"
              )}
            >
              <Icon className="h-4 w-4" />
              {item.label}
            </Link>
          );
        })}
      </div>
    </nav>
  );
}

/* ------------------------------------------------------------------ shell */

export function AppShell({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const { user } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = useCallback(() => setMenuOpen(false), []);
  // One control, two directions. The header's button is the only way in and,
  // now that the bar outranks the sheet, also the thing on screen that looks
  // like the way out — so it TOGGLES. Wiring it open-only made a hamburger
  // that had visibly become an X do nothing when tapped.
  const toggleMenu = useCallback(() => setMenuOpen((open) => !open), []);

  // Auth routes stay chrome-less: a slim brand bar only.
  if (CHROMELESS_ROUTES.has(pathname)) {
    return (
      <div className="flex min-h-screen flex-col bg-shell-canvas">
        <div className="border-b border-border bg-card">
          {/* Theme control sits at the bar's right end, the same slot the
              landing mobile bar gives its toggle — auth pages are otherwise
              the only chrome-less routes with no way to switch theme. */}
          <div className="flex items-center justify-between px-4 py-2 sm:px-6">
            {wordmark("/")}
            <ThemeToggle />
          </div>
        </div>
        {children}
      </div>
    );
  }

  // The admin section carries its own chrome — a dark sidebar with the admin
  // nav, not this app's sidebar — so the shell stands down for it entirely.
  // The layout at app/admin/layout.tsx renders the whole frame.
  if (pathname === "/admin" || pathname.startsWith("/admin/")) {
    return <div className="min-h-screen bg-shell-canvas">{children}</div>;
  }

  // The marketing page owns the whole viewport for a signed-out visitor. Until
  // this pass the Landing rendered inside the full shell — sidebar, bell, the
  // lot — so a stranger's first impression of the product was its logged-in
  // chrome. Reusing the existing auth signal (the same `user` the account block
  // reads) rather than inventing a second one; on the server and on the first
  // client render `user` is null for everyone, so "/" is shell-less in the SSR
  // markup too and a signed-out visitor never sees a frame of sidebar.
  //
  // Trade-off, stated plainly: a SIGNED-IN visitor landing on "/" also gets the
  // shell-less markup first, and the chrome appears once /auth/me resolves.
  // There is no cookie-backed session to read at render time, so the server
  // genuinely cannot know — and of the two possible flashes, showing the app
  // chrome to a logged-out stranger is the one that misrepresents the product.
  if (pathname === "/" && !user) {
    return <>{children}</>;
  }

  return (
    <div className="min-h-screen bg-shell-canvas">
      {/* Desktop: one column. The aside's p-2 supplies the 8px gutter on every
          side, so the panel is flush with the content card's margins. */}
      <aside aria-label="Sidebar" className="fixed inset-y-0 left-0 z-20 hidden w-56 p-2 lg:flex">
        <SidebarPanel />
      </aside>

      <MobileTopBar onToggleMenu={toggleMenu} menuOpen={menuOpen} />
      <MobileDrawer open={menuOpen} onClose={closeMenu} />
      <MobileBottomNav />

      {/* Content card: the second plane on the canvas. The wrapper's p-2 keeps
          its margins equal to the aside's; the page owns any inner bottom
          padding (e.g. the mobile bottom-nav clearance). */}
      <div className="p-2 lg:pl-56">
        <div className="min-h-[calc(100vh-16px)] rounded-card border border-border bg-card">
          {children}
        </div>
      </div>
    </div>
  );
}
